import { useAppMachine } from '~/composables/useAppMachine'
import { useNuxtApp } from '#imports'
import { getYtdFiscalYear } from './financials-grid-commons'

interface FromToDateRange {
  fromDate: Date
  toDate: Date
}

export enum DateRangeDirection {
  FORWARD = 0,
  BACKWARDS = 1,
}

export abstract class DateRangeStrategy {
  readonly $tolgee = useNuxtApp().$tolgee
  readonly locale = useAppMachine().locale.value
  protected inputDate: Date

  abstract get months(): number
  public abstract readonly leftText: string
  public abstract readonly colHeaderName: string
  public abstract readonly id: string

  public rightText: string
  public shortText: string
  public direction: DateRangeDirection

  public dateRange: FromToDateRange

  constructor(inputDate: Date, direction: DateRangeDirection = DateRangeDirection.BACKWARDS) {
    this.inputDate = inputDate
    this.direction = direction
    this.dateRange = this.calculateDateRange()
    this.rightText = this.formatDateRangeToStr()
    this.shortText = this.formatDateRangeToStr()
  }

  protected calculateDateRange(): FromToDateRange {
    const monthIndex = this.months - 1
    if (this.direction === DateRangeDirection.BACKWARDS) {
      const fromDate = new Date(this.inputDate.getFullYear(), this.inputDate.getMonth() - monthIndex, 1) // Start of range
      const toDate = this.inputDate
      return { fromDate, toDate }
    }
    const fromDate = this.inputDate
    const toDate = new Date(this.inputDate.getFullYear(), this.inputDate.getMonth() + monthIndex + 1, 0) // End of range
    return { fromDate, toDate }
  }

  protected formatDateRangeToStr(): string {
    const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short' }

    // Ensure dates are valid before formatting
    if (!this.dateRange.fromDate || Number.isNaN(this.dateRange.fromDate.getTime())) {
      return 'Invalid Date Range'
    }
    if (!this.dateRange.toDate || Number.isNaN(this.dateRange.toDate.getTime())) {
      return 'Invalid Date Range'
    }

    const toDateStr = this.dateRange.toDate.toLocaleDateString(this.locale, options)

    if (this.dateRange.fromDate.getFullYear() === this.dateRange.toDate.getFullYear()) {
      return `${this.dateRange.fromDate.toLocaleDateString(this.locale, { month: 'short' })} - ${toDateStr}`
    }

    const fromDateStr = this.dateRange.fromDate.toLocaleDateString(this.locale, options)
    return `${fromDateStr} - ${toDateStr}`
  }

  public updateData(inputDate: Date | FromToDateRange): void {
    this.inputDate = inputDate as Date
    this.dateRange = this.calculateDateRange()
    this.rightText = this.formatDateRangeToStr()
    this.shortText = this.formatDateRangeToStr()
  }
}

export class LastMonthStrategy extends DateRangeStrategy {
  public get months(): number {
    return 1 // Represents last 1 month
  }
  public override id = 'data-range-last-month'
  public override colHeaderName = this.$tolgee.t('col-header-data-range-last-month')
  public override leftText: string = this.$tolgee.t('data-range-last-month')

  protected override calculateDateRange(): FromToDateRange {
    const fromDate = this.inputDate
    const toDate = this.inputDate
    return { fromDate, toDate }
  }

  protected override formatDateRangeToStr(): string {
    const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short' }
    const toDateStr = this.dateRange.fromDate.toLocaleDateString(this.locale, options)
    return `${toDateStr}`
  }
}

export class Last3MonthsStrategy extends DateRangeStrategy {
  public get months(): number {
    return 3
  }
  public override id = 'data-range-last-3-months'
  public override colHeaderName = this.$tolgee.t('col-header-data-range-last-3-months')
  public override leftText: string = this.$tolgee.t('data-range-last-3-months')
}

export class Last6MonthsStrategy extends DateRangeStrategy {
  public get months(): number {
    return 6
  }
  public override id = 'data-range-last-6-months'
  public override colHeaderName = this.$tolgee.t('col-header-data-range-last-6-months')
  public override leftText: string = this.$tolgee.t('data-range-last-6-months')
}

export class Last12MonthsStrategy extends DateRangeStrategy {
  public get months(): number {
    return 12
  }
  public override id = 'data-range-last-12-months'
  public override colHeaderName = this.$tolgee.t('col-header-data-range-last-12-months')
  public override leftText: string = this.$tolgee.t('data-range-last-12-months')
}

export class Next3MonthsStrategy extends DateRangeStrategy {
  public get months(): number {
    return 3
  }

  public override id = 'data-range-next-3-months'
  public override colHeaderName = this.$tolgee.t('col-header-data-range-next-3-months')
  public override leftText: string = this.$tolgee.t('data-range-next-3-months')

  constructor(inputDate: Date) {
    super(inputDate, DateRangeDirection.FORWARD)
  }
}

export class Next6MonthsStrategy extends DateRangeStrategy {
  public get months(): number {
    return 6
  }

  public override id = 'data-range-next-6-months'
  public override colHeaderName = this.$tolgee.t('col-header-data-range-next-6-months')
  public override leftText: string = this.$tolgee.t('data-range-next-6-months')

  constructor(inputDate: Date) {
    super(inputDate, DateRangeDirection.FORWARD)
  }
}

export class Next12MonthsStrategy extends DateRangeStrategy {
  public get months(): number {
    return 12
  }

  public override id = 'data-range-next-12-months'
  public override colHeaderName = this.$tolgee.t('col-header-data-range-next-12-months')
  public override leftText: string = this.$tolgee.t('data-range-next-12-months')

  constructor(inputDate: Date) {
    super(inputDate, DateRangeDirection.FORWARD)
  }
}

export class YearToDateStrategy extends DateRangeStrategy {
  public get months(): number {
    return 1
  }
  public override id = 'data-range-year-to-date'
  public override colHeaderName = this.$tolgee.t('col-header-data-range-year-to-date')
  public override leftText: string = this.$tolgee.t('data-range-year-to-date')
  private fyeMonth: number

  constructor(inputDate: Date, fyeMonth: number | undefined = 12, direction = DateRangeDirection.BACKWARDS) {
    super(inputDate, direction)
    const numFyeMonth = Number(fyeMonth)
    // Ensure valid fyeMonth (1-12), default to 12 if invalid
    if (Number.isNaN(numFyeMonth) || numFyeMonth < 1 || numFyeMonth > 12) {
      this.fyeMonth = 12
    } else {
      this.fyeMonth = numFyeMonth
    }
    this.direction = DateRangeDirection.BACKWARDS

    // Recalculate dateRange now that fyeMonth is properly set
    this.dateRange = this.calculateDateRange()
    this.rightText = this.formatDateRangeToStr()
    this.shortText = this.formatDateRangeToStr()

    const ytdFiscalYear = getYtdFiscalYear(this.inputDate, this.fyeMonth)
    this.colHeaderName = `${this.$tolgee.t('col-header-data-range-year-to-date')} ${ytdFiscalYear}`
  }

  protected override calculateDateRange(): FromToDateRange {
    // Handle case where fyeMonth might be undefined during parent constructor call
    const effectiveFyeMonth = this.fyeMonth || 12

    // Calculate fiscal year start date based on fiscal year end month
    const currentYear = this.inputDate.getFullYear()
    const currentMonth = this.inputDate.getMonth() + 1 // getMonth() is 0-based, so add 1

    let fiscalYearStartYear = currentYear
    const fiscalYearStartMonth = (effectiveFyeMonth % 12) + 1 // Jan is 1, Dec is 12

    if (currentMonth <= effectiveFyeMonth) {
      fiscalYearStartYear = currentYear - 1
    }

    const fiscalYearStart = new Date(fiscalYearStartYear, fiscalYearStartMonth - 1, 1)

    // Ensure the created date is valid
    if (Number.isNaN(fiscalYearStart.getTime())) {
      // Fallback to January 1st of current year if date is invalid
      return { fromDate: new Date(currentYear, 0, 1), toDate: this.inputDate }
    }

    return { fromDate: fiscalYearStart, toDate: this.inputDate }
  }

  public override updateData(inputDate: Date): void {
    super.updateData(inputDate)
    const ytdFiscalYear = getYtdFiscalYear(this.inputDate, this.fyeMonth)
    this.colHeaderName = `${this.$tolgee.t('col-header-data-range-year-to-date')} ${ytdFiscalYear}`
  }
}

export class Last3ActualsStrategy extends DateRangeStrategy {
  public get months(): number {
    return 3
  }
  public override id = 'data-range-last-3-actuals'
  public override colHeaderName = this.$tolgee.t('col-header-data-range-last-3-actuals')
  public override leftText: string = this.$tolgee.t('data-range-last-3-actuals')

  constructor(inputDate: Date, direction: DateRangeDirection = DateRangeDirection.BACKWARDS) {
    super(inputDate, direction)
    this.colHeaderName = `${this.$tolgee.t('col-header-data-range-last-3-actuals')}`
    // Override the default date range calculation
    this.dateRange = this.calculateDateRange()
    this.rightText = this.formatDateRangeToStr()
    this.shortText = this.formatDateRangeToStr()
  }

  // Override the date range calculation for Last 3 Actuals
  protected override calculateDateRange(): FromToDateRange {
    // For Last 3 Actuals, we want to show the last actual date as the end date
    // and go back 2 months to cover 3 months total
    const toDate = new Date(this.inputDate)
    const fromDate = new Date(this.inputDate.getFullYear(), this.inputDate.getMonth() - 2, 1)
    return { fromDate, toDate }
  }
}

export class CustomDateRangeStrategy extends DateRangeStrategy {
  public get months(): number {
    return 0
  }
  public override id = 'data-range-custom-date'
  public override colHeaderName = this.$tolgee.t('col-header-data-range-custom-date')
  public override leftText: string = this.$tolgee.t('data-range-custom-date')

  constructor(customRange: FromToDateRange, direction: DateRangeDirection = DateRangeDirection.BACKWARDS) {
    super(customRange.toDate, direction)
    this.dateRange = customRange
    this.rightText = '›' // Replaced icon class with a simple chevron character
    this.shortText = this.formatDateRangeToStr()
  }

  override updateData(inputDate: FromToDateRange): void {
    this.dateRange = { fromDate: inputDate.fromDate, toDate: inputDate.toDate }
    this.rightText = '›'
    this.shortText = this.formatDateRangeToStr()
  }
}
